// CrewMarketSystem.js - Hire crew with RPG-style stats
// Crew have: experience, reliability, fatigue tolerance, salary demands
// Better crew = more expensive but fewer problems

// ============================================
// CREW STATS EXPLAINED
// ============================================

// EXPERIENCE (1-100): Flight hours / skill level
//   1-20:   Rookie - Makes mistakes, slower checklists
//   21-50:  Journeyman - Competent, occasional issues
//   51-80:  Veteran - Reliable, handles problems well
//   81-100: Expert - Exceptional performance, mentors others

// RELIABILITY (1-100): Chance they show up / don't call in sick
//   Low reliability = more likely to call in sick, cause delays
//   High reliability = dependable, rarely misses

// FATIGUE_TOLERANCE (1-100): How well they handle long duty periods
//   Low = degrades quickly, needs more rest
//   High = maintains performance under pressure

// SALARY: Monthly cost - correlated with stats

// ============================================
// CREW TEMPLATES
// ============================================

const FIRST_NAMES = [
  'James', 'Robert', 'Michael', 'William', 'David', 'Richard', 'Joseph', 'Thomas',
  'Sarah', 'Jennifer', 'Elizabeth', 'Maria', 'Susan', 'Margaret', 'Dorothy', 'Lisa',
  'Carlos', 'Miguel', 'Hiroshi', 'Yuki', 'Chen', 'Wei', 'Raj', 'Priya',
  'Ahmed', 'Fatima', 'Klaus', 'Ingrid', 'Pierre', 'Marie', 'Ivan', 'Olga'
];

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Anderson', 'Taylor', 'Thomas', 'Moore', 'Jackson', 'White',
  'Nakamura', 'Tanaka', 'Chen', 'Wang', 'Kim', 'Park', 'Patel', 'Singh',
  'Mueller', 'Schmidt', 'Dubois', 'Bernard', 'Petrov', 'Volkov', 'O\'Brien', 'McCarthy'
];

const TYPE_RATINGS = {
  REGIONAL: ['E170', 'E175', 'E190', 'CRJ-700', 'CRJ-900', 'ATR-72', 'Dash-8'],
  NARROWBODY: ['A319', 'A320', 'A321', 'B737-700', 'B737-800', 'B737-900', 'B757'],
  WIDEBODY: ['A330', 'A350', 'A380', 'B767', 'B777', 'B787'],
};

// ============================================
// CREW GENERATION
// ============================================

export function generateCrewMarket(count = 12) {
  const market = [];
  
  for (let i = 0; i < count; i++) {
    market.push(generateCrewMember());
  }
  
  // Sort by salary (cheapest first for player convenience)
  return market.sort((a, b) => a.salary - b.salary);
}

export function generateCrewMember(overrides = {}) {
  // Determine tier (affects stat ranges)
  const tierRoll = Math.random();
  let tier;
  if (tierRoll < 0.4) tier = 'rookie';      // 40% rookies
  else if (tierRoll < 0.75) tier = 'journeyman'; // 35% journeymen
  else if (tierRoll < 0.95) tier = 'veteran';    // 20% veterans
  else tier = 'expert';                          // 5% experts

  // Determine role - flight deck crew vs cabin crew
  const role = overrides.role || determineRole();
  const isCabinCrew = role === 'flight_attendant';

  const stats = generateStats(tier);
  const typeRatings = isCabinCrew ? [] : generateTypeRatings(tier);
  const salary = isCabinCrew
    ? calculateCabinCrewSalary(stats)
    : calculateSalary(stats, typeRatings);

  return {
    id: `crew-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: generateName(),
    role,
    tier,

    // Core stats
    experience: stats.experience,
    reliability: stats.reliability,
    fatigueTolerance: stats.fatigueTolerance,

    // Cabin crew specific stats
    ...(isCabinCrew && {
      serviceRating: randomInRange(tier === 'rookie' ? 40 : tier === 'journeyman' ? 55 : tier === 'veteran' ? 70 : 85,
                                   tier === 'rookie' ? 65 : tier === 'journeyman' ? 80 : tier === 'veteran' ? 95 : 100),
      languages: generateLanguages(tier),
    }),

    // Derived stats
    totalFlightHours: stats.experience * 100 + Math.floor(Math.random() * 500),

    // Qualifications (only for flight deck)
    typeRatings,

    // Contract
    salary, // Monthly
    signingBonus: Math.round(salary * (0.5 + Math.random() * 1.0)), // 0.5-1.5x monthly

    // Personality traits (affect events)
    traits: generateTraits(tier),

    // Current state
    available: true,

    ...overrides,
  };
}

function determineRole() {
  const roll = Math.random();
  if (roll < 0.25) return 'captain';           // 25% captains
  else if (roll < 0.45) return 'first_officer'; // 20% first officers
  else return 'flight_attendant';               // 55% flight attendants
}

function generateLanguages(tier) {
  const languages = ['English'];
  const additionalLanguages = ['Spanish', 'French', 'German', 'Mandarin', 'Japanese', 'Portuguese', 'Korean'];

  const numExtra = tier === 'rookie' ? 0 : tier === 'journeyman' ? randomInRange(0, 1) : randomInRange(1, 2);
  for (let i = 0; i < numExtra; i++) {
    const lang = additionalLanguages[Math.floor(Math.random() * additionalLanguages.length)];
    if (!languages.includes(lang)) languages.push(lang);
  }
  return languages;
}

function calculateCabinCrewSalary(stats) {
  // Flight attendants have lower base salary
  let base = 3000 * 100; // $3K/month minimum

  // Experience adds to salary
  base += stats.experience * 50 * 100; // +$50/month per experience point

  // Reliability premium
  if (stats.reliability > 80) base += 500 * 100;
  if (stats.reliability > 90) base += 1000 * 100;

  // Add some variance
  base = Math.round(base * (0.9 + Math.random() * 0.2));

  return base;
}

function generateStats(tier) {
  const ranges = {
    rookie: { exp: [5, 25], rel: [40, 70], fat: [30, 60] },
    journeyman: { exp: [25, 55], rel: [55, 80], fat: [45, 75] },
    veteran: { exp: [55, 85], rel: [70, 95], fat: [60, 90] },
    expert: { exp: [85, 100], rel: [85, 100], fat: [80, 100] },
  };
  
  const r = ranges[tier];
  
  return {
    experience: randomInRange(r.exp[0], r.exp[1]),
    reliability: randomInRange(r.rel[0], r.rel[1]),
    fatigueTolerance: randomInRange(r.fat[0], r.fat[1]),
  };
}

function generateTypeRatings(tier) {
  const ratings = [];
  
  // Everyone starts with regionals
  const numRegional = tier === 'rookie' ? 1 : randomInRange(1, 3);
  for (let i = 0; i < numRegional; i++) {
    const type = TYPE_RATINGS.REGIONAL[Math.floor(Math.random() * TYPE_RATINGS.REGIONAL.length)];
    if (!ratings.includes(type)) ratings.push(type);
  }
  
  // Journeymen+ get narrowbody
  if (tier !== 'rookie') {
    const numNarrow = tier === 'journeyman' ? randomInRange(0, 2) : randomInRange(1, 3);
    for (let i = 0; i < numNarrow; i++) {
      const type = TYPE_RATINGS.NARROWBODY[Math.floor(Math.random() * TYPE_RATINGS.NARROWBODY.length)];
      if (!ratings.includes(type)) ratings.push(type);
    }
  }
  
  // Veterans+ get widebody
  if (tier === 'veteran' || tier === 'expert') {
    const numWide = tier === 'veteran' ? randomInRange(0, 1) : randomInRange(1, 2);
    for (let i = 0; i < numWide; i++) {
      const type = TYPE_RATINGS.WIDEBODY[Math.floor(Math.random() * TYPE_RATINGS.WIDEBODY.length)];
      if (!ratings.includes(type)) ratings.push(type);
    }
  }
  
  return ratings;
}

function generateTraits(tier) {
  const positiveTraits = [
    'calm_under_pressure',
    'mentor',
    'quick_learner',
    'fuel_efficient',
    'passenger_favorite',
    'early_arriver',
    'team_player',
    'problem_solver',
  ];
  
  const negativeTraits = [
    'hot_headed',
    'lone_wolf',
    'clock_watcher',
    'by_the_book',  // Won't bend rules even when helpful
    'superstitious',
    'late_tendency',
  ];
  
  const traits = [];
  
  // Better tiers get more positive traits
  const numPositive = tier === 'expert' ? 2 : tier === 'veteran' ? 1 : Math.random() > 0.5 ? 1 : 0;
  const numNegative = tier === 'rookie' ? (Math.random() > 0.5 ? 1 : 0) : 0;
  
  for (let i = 0; i < numPositive; i++) {
    const trait = positiveTraits[Math.floor(Math.random() * positiveTraits.length)];
    if (!traits.includes(trait)) traits.push(trait);
  }
  
  for (let i = 0; i < numNegative; i++) {
    const trait = negativeTraits[Math.floor(Math.random() * negativeTraits.length)];
    if (!traits.includes(trait)) traits.push(trait);
  }
  
  return traits;
}

function calculateSalary(stats, typeRatings) {
  // Base salary
  let base = 5000 * 100; // $5K/month minimum
  
  // Experience multiplier
  base += stats.experience * 150 * 100; // +$150/month per experience point
  
  // Reliability premium
  if (stats.reliability > 80) base += 2000 * 100;
  if (stats.reliability > 90) base += 3000 * 100;
  
  // Fatigue tolerance premium
  if (stats.fatigueTolerance > 80) base += 1500 * 100;
  
  // Type rating premiums
  const hasNarrowbody = typeRatings.some(t => TYPE_RATINGS.NARROWBODY.includes(t));
  const hasWidebody = typeRatings.some(t => TYPE_RATINGS.WIDEBODY.includes(t));
  
  if (hasNarrowbody) base += 3000 * 100;
  if (hasWidebody) base += 8000 * 100;
  
  // Add some variance
  base = Math.round(base * (0.9 + Math.random() * 0.2));
  
  return base;
}

function generateName() {
  const first = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const last = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  return `${first} ${last}`;
}

function randomInRange(min, max) {
  return Math.floor(min + Math.random() * (max - min + 1));
}

// ============================================
// CREW STATE MANAGEMENT
// ============================================

export function createCrewState() {
  return {
    roster: [],           // Hired crew
    market: generateCrewMarket(12),
    totalMonthlySalary: 0,
  };
}

export function hireCrew(state, crewId, cash) {
  const crew = state.market.find(c => c.id === crewId);
  if (!crew) {
    return { success: false, error: 'Crew member not found' };
  }
  
  const totalCost = crew.salary + crew.signingBonus;
  if (cash < totalCost) {
    return { success: false, error: 'Insufficient funds for signing bonus' };
  }
  
  const newState = {
    ...state,
    roster: [...state.roster, { ...crew, hiredAt: Date.now() }],
    market: state.market.filter(c => c.id !== crewId),
    totalMonthlySalary: state.totalMonthlySalary + crew.salary,
  };
  
  return {
    success: true,
    state: newState,
    cost: totalCost,
    message: `${crew.name} has joined your crew.`,
  };
}

export function fireCrew(state, crewId) {
  const crew = state.roster.find(c => c.id === crewId);
  if (!crew) {
    return { success: false, error: 'Crew member not on roster' };
  }
  
  // Severance = 2 months salary
  const severance = crew.salary * 2;
  
  const newState = {
    ...state,
    roster: state.roster.filter(c => c.id !== crewId),
    totalMonthlySalary: state.totalMonthlySalary - crew.salary,
  };
  
  return {
    success: true,
    state: newState,
    cost: severance,
    message: `${crew.name} has been let go. Severance: $${formatMoney(severance)}`,
  };
}

export function refreshCrewMarket(state) {
  // Called monthly or on demand - brings in new candidates
  const newCandidates = generateCrewMarket(4);
  
  // Keep some old candidates (they don't all leave immediately)
  const remaining = state.market.filter(() => Math.random() > 0.3);
  
  return {
    ...state,
    market: [...remaining, ...newCandidates].slice(0, 15), // Cap at 15
  };
}

export function processMonthlyCrewCosts(state) {
  return {
    state,
    totalCost: state.totalMonthlySalary,
    breakdown: state.roster.map(c => ({
      name: c.name,
      role: c.role,
      salary: c.salary,
    })),
  };
}

// ============================================
// CREW ASSIGNMENT HELPERS
// ============================================

export function getAvailableCrewForAircraft(roster, aircraftType, location) {
  return roster.filter(crew => {
    // Must have type rating
    if (!crew.typeRatings.includes(aircraftType)) return false;
    
    // Must be at location (simplified - assume they can be anywhere for now)
    // TODO: Track crew locations
    
    // Must be available (not on another flight, not resting)
    if (!crew.available) return false;
    
    return true;
  });
}

export function getCrewPerformanceModifier(crew) {
  // Returns a modifier affecting flight outcomes
  // Based on experience, reliability, and traits
  
  let modifier = 1.0;
  
  // Experience affects OTP
  modifier += (crew.experience - 50) * 0.002; // ±10% based on experience
  
  // Reliability affects whether problems happen
  modifier += (crew.reliability - 70) * 0.001;
  
  // Traits
  if (crew.traits.includes('calm_under_pressure')) modifier += 0.05;
  if (crew.traits.includes('problem_solver')) modifier += 0.03;
  if (crew.traits.includes('hot_headed')) modifier -= 0.03;
  if (crew.traits.includes('late_tendency')) modifier -= 0.02;
  
  return Math.max(0.7, Math.min(1.3, modifier)); // Clamp to ±30%
}

export function rollCrewCallIn(crew) {
  // Check if crew calls in sick/unavailable
  // Lower reliability = higher chance of calling in
  const callInChance = (100 - crew.reliability) / 100 * 0.1; // Max 10% for 0 reliability
  return Math.random() < callInChance;
}

// ============================================
// UTILITY
// ============================================

function formatMoney(cents) {
  if (cents >= 100000000) {
    return `${(cents / 100000000).toFixed(1)}M`;
  } else if (cents >= 100000) {
    return `${(cents / 100000).toFixed(0)}K`;
  }
  return (cents / 100).toLocaleString();
}

// ============================================
// EXPORTS
// ============================================

export default {
  generateCrewMarket,
  generateCrewMember,
  createCrewState,
  hireCrew,
  fireCrew,
  refreshCrewMarket,
  processMonthlyCrewCosts,
  getAvailableCrewForAircraft,
  getCrewPerformanceModifier,
  rollCrewCallIn,
  TYPE_RATINGS,
};
